import { normalizeLang, type Lang } from "@/i18n/locale";

/** Software playback signals only. No content, identity, URL or absolute clock
 * leaves this collector. The caller owns the auth/turn guard and DOM commit. */
export const MIRROR_SPEECH_MAX_MS = 600_000;
export const MIRROR_SPEECH_TIMING_KEYS = [
  "schema_version", "platform", "app_version", "tier", "language", "network",
  "mode", "trigger", "voice_enabled", "response_length_bucket", "cache_proof",
  "terminal", "terminal_reason", "synthetic",
  "send_to_first_visible_text_ms", "send_to_stable_final_text_ms",
  "send_to_response_complete_ms", "send_to_tts_request_ms", "send_to_audio_ready_ms",
  "send_to_html_playing_ms", "tts_request_to_audio_ready_ms",
  "audio_ready_to_html_playing_ms", "total_ms",
] as const;

export type MirrorSpeechTerminal = "complete" | "stop" | "error" | "noaudio";
export type MirrorSpeechTerminalReason =
  | "ended" | "stop" | "superseded" | "unmount" | "tts_quota" | "tts_request"
  | "playback" | "audio_error" | "voice_disabled" | "empty_response"
  | "response_quota" | "response_error" | "response_cancelled" | "unknown";
export type MirrorSpeechObservation =
  | { stage: "request_start" }
  | { stage: "audio_ready"; cached: boolean | null }
  | { stage: "playing" }
  | { stage: "ended" }
  | { stage: "error"; code: "tts_quota" | "tts_request" | "playback" | "audio_error" }
  | { stage: "cancelled"; code: "stop" | "superseded" | "owner_changed" | "unmount" };
export type MirrorSpeechObserver = (event: MirrorSpeechObservation) => void;

export interface MirrorSpeechTimingReport {
  readonly schema_version: 1;
  readonly platform: "web" | "ios" | "android" | "unknown";
  readonly app_version: string;
  readonly tier: "free" | "premium" | "unknown";
  readonly language: Lang | "unknown";
  readonly network: "wifi" | "cellular" | "ethernet" | "offline" | "unknown";
  readonly mode: "normal" | "turn_voice" | "manual_listen";
  readonly trigger: "autoplay" | "manual";
  readonly voice_enabled: boolean | null;
  readonly response_length_bucket: "empty" | "short" | "medium" | "long" | "unknown";
  readonly cache_proof: "hit" | "miss" | "unknown";
  readonly terminal: MirrorSpeechTerminal;
  readonly terminal_reason: MirrorSpeechTerminalReason;
  readonly synthetic: boolean;
  readonly send_to_first_visible_text_ms: number | null;
  readonly send_to_stable_final_text_ms: number | null;
  readonly send_to_response_complete_ms: number | null;
  readonly send_to_tts_request_ms: number | null;
  readonly send_to_audio_ready_ms: number | null;
  readonly send_to_html_playing_ms: number | null;
  readonly tts_request_to_audio_ready_ms: number | null;
  readonly audio_ready_to_html_playing_ms: number | null;
  readonly total_ms: number | null;
}

export interface MirrorSpeechMetadata {
  platform?: unknown;
  app_version?: unknown;
  tier?: unknown;
  language?: unknown;
  network?: unknown;
  mode?: unknown;
  trigger?: unknown;
  voice_enabled?: unknown;
  synthetic?: unknown;
}
export interface MirrorSpeechMetrics {
  acceptedSend(): void;
  firstVisibleText(): void;
  /** Actual FINAL frame, not a fabricated early stable chunk or DOM timestamp. */
  stableFinalText(charCount: number): void;
  responseComplete(): void;
  readonly observer: MirrorSpeechObserver;
  finish(terminal: MirrorSpeechTerminal, reason: MirrorSpeechTerminalReason): void;
  /** Owner/turn revocation suppresses the origin forever, including ABA. */
  discard(): void;
}
const platforms = ["web", "ios", "android", "unknown"] as const;
const tiers = ["free", "premium", "unknown"] as const;
const networks = ["wifi", "cellular", "ethernet", "offline", "unknown"] as const;
const modes = ["normal", "turn_voice", "manual_listen"] as const;
const triggers = ["autoplay", "manual"] as const;
const lengths = ["empty", "short", "medium", "long", "unknown"] as const;
const caches = ["hit", "miss", "unknown"] as const;
const terminals = ["complete", "stop", "error", "noaudio"] as const;
const reasons = ["ended", "stop", "superseded", "unmount", "tts_quota", "tts_request",
  "playback", "audio_error", "voice_disabled", "empty_response", "response_quota",
  "response_error", "response_cancelled", "unknown"] as const;
const timeKeys = MIRROR_SPEECH_TIMING_KEYS.filter(key => key.endsWith("_ms"));
const member = <T extends string>(values: readonly T[], value: unknown): value is T =>
  typeof value === "string" && values.some(item => item === value);
const duration = (value: unknown): value is number | null =>
  value === null || typeof value === "number" && Number.isFinite(value)
    && value >= 0 && value <= MIRROR_SPEECH_MAX_MS;
const version = (value: unknown): value is string =>
  value === "unknown" || typeof value === "string" && /^[0-9a-f]{7,40}$/.test(value);

/** Strict shared decoder for the anonymous transport and report tooling.
 * Extra keys fail closed; callers cannot smuggle a message through metadata. */
export function validateMirrorSpeechTimingReport(value: unknown): MirrorSpeechTimingReport | null {
  try {
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    const row = value as Record<string, unknown>;
    const keys = Object.keys(row);
    if (keys.length !== MIRROR_SPEECH_TIMING_KEYS.length
      || !MIRROR_SPEECH_TIMING_KEYS.every(key => Object.prototype.hasOwnProperty.call(row, key))) return null;
    if (row.schema_version !== 1 || !member(platforms, row.platform) || !version(row.app_version)
      || !member(tiers, row.tier) || !(row.language === "unknown" || typeof row.language === "string" && normalizeLang(row.language) === row.language)
      || !member(networks, row.network) || !member(modes, row.mode) || !member(triggers, row.trigger)
      || !(row.voice_enabled === null || typeof row.voice_enabled === "boolean")
      || !member(lengths, row.response_length_bucket) || !member(caches, row.cache_proof)
      || !member(terminals, row.terminal) || !member(reasons, row.terminal_reason)
      || typeof row.synthetic !== "boolean" || !timeKeys.every(key => duration(row[key]))) return null;
    return Object.freeze({ ...row }) as unknown as MirrorSpeechTimingReport;
  } catch { return null; }
}

export function mirrorSpeechResponseLength(charCount: number): MirrorSpeechTimingReport["response_length_bucket"] {
  if (!Number.isSafeInteger(charCount) || charCount < 0) return "unknown";
  return charCount === 0 ? "empty" : charCount <= 280 ? "short" : charCount <= 1000 ? "medium" : "long";
}

export function createMirrorSpeechMetrics(options: {
  metadata: MirrorSpeechMetadata;
  isCurrent: () => boolean;
  onResult: (report: MirrorSpeechTimingReport) => void;
  visibleTextExpected: boolean;
  now?: () => number;
}): MirrorSpeechMetrics {
  const raw = options.metadata;
  const metadata = {
    schema_version: 1 as const,
    platform: member(platforms, raw.platform) ? raw.platform : "unknown" as const,
    app_version: version(raw.app_version) ? raw.app_version : "unknown",
    tier: member(tiers, raw.tier) ? raw.tier : "unknown" as const,
    language: typeof raw.language === "string" ? normalizeLang(raw.language) ?? "unknown" : "unknown" as const,
    network: member(networks, raw.network) ? raw.network : "unknown" as const,
    mode: member(modes, raw.mode) ? raw.mode : "normal" as const,
    trigger: member(triggers, raw.trigger) ? raw.trigger : "autoplay" as const,
    voice_enabled: typeof raw.voice_enabled === "boolean" ? raw.voice_enabled : null,
    synthetic: raw.synthetic === true,
  };
  const clock = options.now ?? (() => performance.now());
  let closed = false, accepted = false;
  let start: number | null = null, lastClock: number | null = null;
  let charCount: number | null = null;
  let responseLength: MirrorSpeechTimingReport["response_length_bucket"] = "unknown";
  let cacheProof: MirrorSpeechTimingReport["cache_proof"] = "unknown";
  type Point = "visible" | "stable" | "response" | "request" | "audio" | "playing";
  const seen = new Set<Point>();
  const points: Partial<Record<Point, number | null>> = {};
  let pending: { terminal: MirrorSpeechTerminal; reason: MirrorSpeechTerminalReason; at: number | null } | null = null;
  const current = () => {
    if (closed) return false;
    try { if (options.isCurrent()) return true; } catch { /* Invalid guard is revocation. */ }
    closed = true; pending = null; return false;
  };
  const readClock = (): number | null => {
    try {
      const value = clock();
      if (!Number.isFinite(value) || value < 0 || lastClock !== null && value < lastClock) return null;
      lastClock = value; return value;
    } catch { return null; }
  };
  const elapsed = (from: number | null | undefined, to: number | null | undefined): number | null => {
    if (from == null || to == null) return null;
    const value = to - from;
    return duration(value) ? value : null;
  };
  const point = (name: Point) => {
    if (!current() || !accepted || seen.has(name)) return;
    seen.add(name); points[name] = readClock();
  };
  const flush = () => {
    if (!pending || !current()) return;
    if ((pending.terminal === "complete" || pending.terminal === "noaudio" && pending.reason === "voice_disabled" && charCount !== null)
      && options.visibleTextExpected && charCount !== 0 && !seen.has("visible")) return;
    const result = validateMirrorSpeechTimingReport({
      ...metadata, response_length_bucket: responseLength, cache_proof: cacheProof,
      terminal: pending.terminal, terminal_reason: pending.reason,
      send_to_first_visible_text_ms: elapsed(start, points.visible),
      send_to_stable_final_text_ms: elapsed(start, points.stable),
      send_to_response_complete_ms: elapsed(start, points.response),
      send_to_tts_request_ms: elapsed(start, points.request),
      send_to_audio_ready_ms: elapsed(start, points.audio),
      send_to_html_playing_ms: elapsed(start, points.playing),
      tts_request_to_audio_ready_ms: elapsed(points.request, points.audio),
      audio_ready_to_html_playing_ms: elapsed(points.audio, points.playing),
      total_ms: elapsed(start, pending.at),
    });
    closed = true; pending = null;
    if (result) try { void Promise.resolve(options.onResult(result)).catch(() => {}); } catch { /* Metrics never affect speech. */ }
  };
  const finish = (terminal: MirrorSpeechTerminal, reason: MirrorSpeechTerminalReason) => {
    if (!current() || !accepted) return;
    if (pending && !(terminal === "stop" || terminal === "error")) return;
    if (!member(terminals, terminal) || !member(reasons, reason)) return;
    pending = { terminal, reason, at: readClock() }; flush();
  };
  const observer: MirrorSpeechObserver = event => {
    if (!current() || !accepted || pending && event.stage !== "error" && event.stage !== "cancelled") return;
    switch (event.stage) {
      case "request_start": point("request"); break;
      case "audio_ready":
        if (!seen.has("request") || seen.has("audio")) return;
        cacheProof = event.cached === true ? "hit" : event.cached === false ? "miss" : "unknown";
        point("audio"); break;
      case "playing":
        if (seen.has("audio")) point("playing");
        break;
      case "ended": finish("complete", "ended"); break;
      case "error": finish("error", event.code); break;
      case "cancelled":
        if (event.code === "owner_changed") { closed = true; pending = null; }
        else finish("stop", event.code);
        break;
    }
  };
  return {
    acceptedSend() { if (!current() || accepted) return; accepted = true; start = readClock(); },
    firstVisibleText() { point("visible"); flush(); },
    stableFinalText(count) {
      if (!current() || !accepted || seen.has("stable")) return;
      charCount = Number.isSafeInteger(count) && count >= 0 ? count : null;
      responseLength = mirrorSpeechResponseLength(count); point("stable"); flush();
    },
    responseComplete() { point("response"); },
    observer, finish,
    discard() { closed = true; pending = null; },
  };
}
